/**
 * Portal Invite Accept Page — /portal-invite/:token
 *
 * A token-gated landing page that lets an invited band/troupe member link their
 * authenticated Replit account to a talent_member record, giving them portal
 * access at the level the primary contact configured (read or write).
 */
import { useState } from "react";
import { useLocation, useParams } from "wouter";
import { useQuery, useMutation } from "@tanstack/react-query";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Loader2, CheckCircle, XCircle, Users } from "lucide-react";
import { apiRequest } from "@/lib/queryClient";
import { useToast } from "@/hooks/use-toast";
import type { UserProfile } from "@shared/schema";

type InviteInfo = {
  memberName: string;
  talentName: string;
  workspaceName: string;
  accessLevel: "read" | "write";
  expiresAt: string | null;
};

export default function PortalInvitePage() {
  const { token } = useParams<{ token: string }>();
  const [, navigate] = useLocation();
  const { toast } = useToast();
  const [accepted, setAccepted] = useState(false);

  const { data: profile } = useQuery<UserProfile>({ queryKey: ["/api/profile"] });

  const { data: invite, isLoading, error } = useQuery<InviteInfo>({
    queryKey: ["/api/portal-invite", token],
    queryFn: async () => {
      const r = await fetch(`/api/portal-invite/${token}`, { credentials: "include" });
      if (!r.ok) {
        const body = await r.json().catch(() => ({}));
        throw new Error(body.message || "Invalid or expired invite");
      }
      return r.json();
    },
    enabled: !!token && !!profile,
    retry: false,
  });

  const acceptMutation = useMutation({
    mutationFn: async () => {
      const r = await apiRequest("POST", `/api/portal-invite/${token}/accept`);
      return r.json();
    },
    onSuccess: (data) => {
      setAccepted(true);
      toast({ title: "Invitation accepted!", description: `You now have access to the ${data.talentName} portal.` });
      setTimeout(() => navigate("/artist"), 2000);
    },
    onError: (err: any) => {
      toast({ title: "Error", description: err?.message || "Failed to accept invite", variant: "destructive" });
    },
  });

  // If not logged in, redirect to login with return URL
  if (!profile) {
    return (
      <div className="min-h-screen flex items-center justify-center p-4">
        <Card className="max-w-md w-full">
          <CardHeader>
            <CardTitle>Sign in to accept your invitation</CardTitle>
            <CardDescription>
              You need to be signed in to accept a portal invitation.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <Button
              className="w-full"
              onClick={() => {
                window.location.href = `/api/login?silent=1&returnUrl=${encodeURIComponent(window.location.pathname)}`;
              }}
              data-testid="button-sign-in-invite"
            >
              Sign In
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (error || !invite) {
    return (
      <div className="min-h-screen flex items-center justify-center p-4">
        <Card className="max-w-md w-full">
          <CardHeader>
            <div className="flex items-center gap-3">
              <XCircle className="h-8 w-8 text-destructive" />
              <div>
                <CardTitle>Invite not found</CardTitle>
                <CardDescription>
                  {(error as any)?.message || "This invite link is invalid or has already been used."}
                </CardDescription>
              </div>
            </div>
          </CardHeader>
        </Card>
      </div>
    );
  }

  if (accepted) {
    return (
      <div className="min-h-screen flex items-center justify-center p-4">
        <Card className="max-w-md w-full">
          <CardHeader>
            <div className="flex items-center gap-3">
              <CheckCircle className="h-8 w-8 text-green-500" />
              <div>
                <CardTitle>You're in!</CardTitle>
                <CardDescription>
                  Redirecting you to the {invite.talentName} portal…
                </CardDescription>
              </div>
            </div>
          </CardHeader>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center p-4 bg-muted/30">
      <Card className="max-w-md w-full">
        <CardHeader>
          <div className="flex items-center gap-3 mb-2">
            <div className="h-12 w-12 rounded-full bg-primary/10 flex items-center justify-center">
              <Users className="h-6 w-6 text-primary" />
            </div>
            <div>
              <CardTitle>Artist Portal Invitation</CardTitle>
              <CardDescription>{invite.workspaceName}</CardDescription>
            </div>
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          <p className="text-sm">
            Hi <strong>{invite.memberName}</strong>, you've been invited to access the artist portal for{" "}
            <strong>{invite.talentName}</strong>.
          </p>
          <div className="rounded-lg border bg-muted/50 p-3 text-sm space-y-1">
            <div className="flex justify-between">
              <span className="text-muted-foreground">Access level</span>
              <span className="font-medium capitalize">
                {invite.accessLevel === "write" ? "Read & Write" : "Read Only"}
              </span>
            </div>
            {invite.expiresAt && (
              <div className="flex justify-between">
                <span className="text-muted-foreground">Invite expires</span>
                <span className="font-medium">
                  {new Date(invite.expiresAt).toLocaleDateString()}
                </span>
              </div>
            )}
          </div>
          <p className="text-xs text-muted-foreground">
            You're signing in as <strong>{profile?.email || profile?.username}</strong>. This account will be
            permanently linked to the <strong>{invite.memberName}</strong> member record.
          </p>
          <Button
            className="w-full"
            onClick={() => acceptMutation.mutate()}
            disabled={acceptMutation.isPending}
            data-testid="button-accept-invite"
          >
            {acceptMutation.isPending ? (
              <><Loader2 className="h-4 w-4 mr-2 animate-spin" />Accepting…</>
            ) : (
              "Accept Invitation"
            )}
          </Button>
        </CardContent>
      </Card>
    </div>
  );
}
